/**
 * Datenbank-Blöcke (docs/KONZEPT.md, Abschnitt 9).
 *
 * Kernidee: Eine Datenbank ist keine eigene Entität, sondern eine gespeicherte Abfrage
 * über Notizen plus Spaltendefinition. Die Zeilen *sind* normale Notizen, die Spalten
 * *sind* Frontmatter-Felder. Dadurch bleibt alles Markdown und exportierbar.
 */

export type ColumnType = 'text' | 'number' | 'date' | 'select' | 'multi' | 'checkbox' | 'tags' | 'url';

export type DbColumn = {
  key: string;
  label: string;
  type: ColumnType;
  options: string[];
};

export type FromClause =
  | { kind: 'all' }
  | { kind: 'folder'; name: string }
  | { kind: 'tag'; name: string };

export type Comparison = '=' | '!=' | '>' | '<' | '>=' | '<=' | 'contains' | 'empty' | 'not empty';

export type DbFilter = { key: string; op: Comparison; value: string };

export type DbConfig = {
  from: FromClause[];
  columns: DbColumn[];
  filters: DbFilter[];
  sort: { key: string; direction: 'asc' | 'desc' } | null;
  group: string | null;
  limit: number | null;
  errors: string[];
};

const TYPES: ColumnType[] = ['text', 'number', 'date', 'select', 'multi', 'checkbox', 'tags', 'url'];

function parseFrom(value: string, errors: string[]): FromClause[] {
  const clauses: FromClause[] = [];
  for (const part of value.split(/\s+and\s+/i)) {
    const trimmed = part.trim();
    if (trimmed === 'all' || trimmed === 'alle') {
      clauses.push({ kind: 'all' });
      continue;
    }
    const match = /^(folder|ordner|tag)\s*\(\s*"?([^")]*)"?\s*\)$/i.exec(trimmed);
    if (!match) {
      errors.push(`Unbekannte Quelle: ${trimmed}`);
      continue;
    }
    const kind = match[1]!.toLowerCase();
    clauses.push(
      kind === 'tag'
        ? { kind: 'tag', name: (match[2] ?? '').toLowerCase() }
        : { kind: 'folder', name: match[2] ?? '' },
    );
  }
  return clauses.length > 0 ? clauses : [{ kind: 'all' }];
}

function parseColumn(line: string, errors: string[]): DbColumn | null {
  const body = line.replace(/^\s*-\s*/, '').trim();
  if (!body) return null;
  const colon = body.indexOf(':');
  if (colon < 0) return { key: body, label: body, type: 'text', options: [] };

  const key = body.slice(0, colon).trim();
  const rest = body.slice(colon + 1).trim();
  const optionsMatch = /\[([^\]]*)\]/.exec(rest);
  const typeName = rest.replace(/\[[^\]]*\]/, '').trim().toLowerCase();
  const type = TYPES.includes(typeName as ColumnType) ? (typeName as ColumnType) : 'text';
  if (typeName && !TYPES.includes(typeName as ColumnType)) {
    errors.push(`Unbekannter Spaltentyp: ${typeName}`);
  }
  return {
    key,
    label: key,
    type,
    options: optionsMatch
      ? (optionsMatch[1] ?? '')
          .split(',')
          .map((option) => option.trim())
          .filter(Boolean)
      : [],
  };
}

function parseFilter(value: string, errors: string[]): DbFilter | null {
  const empty = /^(.+?)\s+(not\s+empty|empty|leer|nicht\s+leer)$/i.exec(value);
  if (empty) {
    const word = empty[2]!.toLowerCase();
    return {
      key: empty[1]!.trim(),
      op: word === 'empty' || word === 'leer' ? 'empty' : 'not empty',
      value: '',
    };
  }
  const match = /^(.+?)\s*(!=|>=|<=|=|>|<|contains|enthält)\s*(.*)$/i.exec(value);
  if (!match) {
    errors.push(`Filter nicht verstanden: ${value}`);
    return null;
  }
  const operator = match[2]!.toLowerCase();
  return {
    key: match[1]!.trim(),
    op: (operator === 'enthält' ? 'contains' : operator) as Comparison,
    value: match[3]!.trim().replace(/^"|"$/g, ''),
  };
}

export function parseConfig(source: string): DbConfig {
  const errors: string[] = [];
  const config: DbConfig = {
    from: [{ kind: 'all' }],
    columns: [],
    filters: [],
    sort: null,
    group: null,
    limit: null,
    errors,
  };

  const lines = source.split(/\r?\n/);
  let inColumns = false;

  for (const line of lines) {
    if (!line.trim()) continue;
    if (/^\s+-/.test(line) && inColumns) {
      const column = parseColumn(line, errors);
      if (column) config.columns.push(column);
      continue;
    }
    inColumns = false;

    const colon = line.indexOf(':');
    if (colon < 0) continue;
    const key = line.slice(0, colon).trim().toLowerCase();
    const value = line.slice(colon + 1).trim();

    switch (key) {
      case 'from':
      case 'quelle':
        config.from = parseFrom(value, errors);
        break;
      case 'columns':
      case 'spalten':
        inColumns = true;
        break;
      case 'filter': {
        const filter = parseFilter(value, errors);
        if (filter) config.filters.push(filter);
        break;
      }
      case 'sort':
      case 'sortierung': {
        const [sortKey, direction] = value.split(/\s+/);
        if (sortKey) {
          config.sort = {
            key: sortKey,
            direction: (direction ?? 'asc').toLowerCase().startsWith('desc') ? 'desc' : 'asc',
          };
        }
        break;
      }
      case 'group':
      case 'gruppierung':
        config.group = value || null;
        break;
      case 'limit':
        config.limit = Number.isFinite(Number(value)) ? Number(value) : null;
        break;
      default:
        errors.push(`Unbekannte Angabe: ${key}`);
    }
  }

  if (config.columns.length === 0) {
    config.columns.push({ key: 'title', label: 'Titel', type: 'text', options: [] });
  }
  return config;
}
